/**
 * Web push, in one place: the SDK, what the browser supports, and the truth
 * about whether this device is subscribed.
 *
 * It lived inside PushOptIn.jsx, which was fine while the invitation card was
 * the only thing that touched OneSignal. It stopped being fine the moment a
 * toggle had to report the same state from a dashboard: `OneSignal.init()` may
 * run exactly once per page, so two components each holding their own loader is
 * a race, not a duplication.
 *
 * Everything here is deliberately readable rather than clever, because the
 * failures it has to survive are configuration failures — an origin that does
 * not match the app, a CDN that is blocked, a platform that cannot do push at
 * all. None of them is the user's to fix, so none of them reaches the screen:
 * every one comes back as a named state the caller can act on and, where it is
 * a genuine failure, as a `console.warn` for whoever is reading the console.
 */
import api from "@/lib/api";

export const ONESIGNAL_APP_ID = (process.env.REACT_APP_ONESIGNAL_APP_ID || "").trim();

/**
 * The origin this app is published at, read from the environment.
 *
 * OneSignal binds an app id to one origin and refuses everything else with
 * `Can only be used on: <origin>` — a sentence that is true, useless to the
 * person reading it, and the reason the invitation card used to show a
 * technical error to someone who had done nothing wrong. The apex redirects to
 * www at Cloudflare, but a stale bookmark, a cached page, or the Railway URL
 * itself all still land on an origin the SDK will reject, so the check happens
 * here rather than being left to the SDK's error message.
 *
 * Empty when unset, and empty means "do not compare": a build without the
 * variable behaves exactly as it did before, including local development.
 */
export const CANONICAL_ORIGIN = (() => {
  const raw = (process.env.REACT_APP_PUBLIC_URL || "").trim();
  if (!raw) return "";
  try {
    return new URL(raw).origin;
  } catch {
    return "";
  }
})();

let _originWarned = false;

/** Whether push can work at all from where the page is currently served. */
export function isCanonicalOrigin() {
  if (!CANONICAL_ORIGIN) return true;
  if (window.location.origin === CANONICAL_ORIGIN) return true;
  if (!_originWarned) {
    _originWarned = true;
    // Once per page: `pushSupport()` is called from render.
    console.warn(
      "Push disabled: non-canonical origin",
      { origin: window.location.origin, expected: CANONICAL_ORIGIN }
    );
  }
  return false;
}

/** Running from the home screen rather than inside a browser tab. */
export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // Safari's own flag, which is the only one iOS sets.
    window.navigator?.standalone === true
  );
}

export function isIOS() {
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1)
  );
}

/**
 * What this device can do about push, as one of four answers.
 *
 * `"ios-needs-install"` is the one worth naming. iOS gained web push in 16.4
 * and gave it only to sites launched from the home screen: inside Safari the
 * `Notification` constructor does not exist at all, so a support check that
 * only asks `"Notification" in window` concludes "unsupported" and shows
 * nothing — which is what PushOptIn did, and why an iPhone saw no invitation
 * whether or not the PWA was installed. The honest answer there is not "your
 * phone cannot do this". It is "add it to your home screen first".
 */
export function pushSupport() {
  if (!ONESIGNAL_APP_ID) return "unconfigured";
  // Same answer as a missing app id, and for the same reason: there is nothing
  // here that a person could act on, so nothing is offered.
  if (!isCanonicalOrigin()) return "unconfigured";
  if ("Notification" in window && "serviceWorker" in navigator) return "supported";
  if (isIOS() && !isStandalone()) return "ios-needs-install";
  return "unsupported";
}

export function permissionState() {
  return "Notification" in window ? Notification.permission : "default";
}

// --- the SDK, loaded and initialised exactly once ------------------------

let _initPromise = null;
const SDK_TIMEOUT_MS = 15000;

export function loadOneSignal() {
  if (_initPromise) return _initPromise;
  _initPromise = new Promise((resolve, reject) => {
    if (!ONESIGNAL_APP_ID) return reject(new Error("Missing OneSignal app id"));
    // Refused before the script tag: the SDK would reject this origin anyway,
    // after a CDN round trip and with a message meant for us, not for the user.
    if (!isCanonicalOrigin()) return reject(new Error("Non-canonical origin"));

    const timer = setTimeout(
      () => reject(new Error("OneSignal SDK did not load (network or CDN blocked)")),
      SDK_TIMEOUT_MS
    );

    window.OneSignalDeferred = window.OneSignalDeferred || [];
    const existing = document.querySelector('script[src*="OneSignalSDK.page.js"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
      s.defer = true;
      s.onerror = () => reject(new Error("OneSignal SDK script failed to load"));
      document.head.appendChild(s);
    }
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.init({
          appId: ONESIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          // Without this the SDK ignores both lines below and registers at the
          // dashboard's registrationScope, which is "/" — the scope index.html
          // already gives /service-worker.js. One scope holds one registration,
          // so OneSignal would replace the PWA's offline worker.
          serviceWorkerOverrideForTypical: true,
          serviceWorkerParam: { scope: "/onesignal/" },
          serviceWorkerPath: "OneSignalSDKWorker.js",
        });
        clearTimeout(timer);
        // The FixyMatch chime rides on any path that initialises the SDK — the
        // invitation card, the dashboard toggle, a silent re-subscribe — so it
        // is attached here rather than by whichever one happened to run first.
        attachBoostChime(OneSignal);
        attachClickNavigation(OneSignal);
        resolve(OneSignal);
      } catch (e) {
        clearTimeout(timer);
        reject(e);
      }
    });
  });
  // A rejected init must not be cached for the life of the page: the first
  // failure would otherwise make every later attempt fail instantly without
  // retrying, including failures that are transient.
  _initPromise.catch(() => { _initPromise = null; });
  return _initPromise;
}

// --- subscription state and the two transitions --------------------------

/**
 * Whether this browser is actually subscribed, asked of the SDK.
 *
 * Not of localStorage, and that is the point. A flag we wrote last week says
 * what we believed then; `optedIn` and the subscription id say what OneSignal
 * will do with the next notification. They come apart in ordinary ways — the
 * browser was reset, the site data was cleared, the user turned it off in
 * another tab — and every one of those is a case where a local flag would show
 * a toggle that lies.
 */
export async function readSubscription() {
  const support = pushSupport();
  if (support !== "supported") return { state: support, id: null };
  if (permissionState() === "denied") return { state: "denied", id: null };
  try {
    const OneSignal = await loadOneSignal();
    const sub = OneSignal.User?.PushSubscription;
    const id = sub?.id || null;
    const optedIn = !!sub?.optedIn;
    return { state: optedIn && id ? "on" : "off", id };
  } catch (e) {
    return { state: "error", id: null, error: e };
  }
}

// --- identity: making sure the device knows whose it is ------------------

/**
 * `<externalId>:<subscriptionId>`, written once the two have been bound.
 *
 * Not a cache of the answer — a record of the work. Binding is two network
 * calls, and running them on every page load of every session would be a lot of
 * traffic to re-establish something that does not change. The subscription id
 * is in the key on purpose: a browser that re-subscribes gets a new one, the
 * mark stops matching, and the binding is done again.
 */
const IDENTITY_KEY = "fd_push_identity";

function readIdentityMark() {
  try { return localStorage.getItem(IDENTITY_KEY); } catch { return null; }
}

function writeIdentityMark(value) {
  try { localStorage.setItem(IDENTITY_KEY, value); } catch { /* storage off */ }
}

let _identityInFlight = null;

/**
 * Bind this device to this account at OneSignal, if it is not bound already.
 *
 * **The bug this exists for.** `login()` only ever ran inside `enablePush`, so
 * it reached exactly the devices that subscribed after it shipped. Every
 * subscription created before that — four of the five rows on 2026-08-21, read
 * back from OneSignal one by one — belonged to an anonymous user with no
 * External ID:
 * a live subscription that no tag filter can name, because tags are written
 * against our user id (services/push_tags.py). Nothing in the app ever revisited
 * that, so those devices were permanently outside every audience but "everyone",
 * and the only repair on offer was for each person to turn push off and on
 * again — which nobody is going to do for a problem they cannot see.
 *
 * So the binding is no longer a step inside subscribing. It is a thing that is
 * true or not true about this device right now, checked when a signed-in person
 * loads the site, and made true if it is not. The five repair themselves on the
 * next visit, one visit each, with nobody asked to do anything.
 *
 * Deliberately silent and deliberately unable to fail anything: it returns a
 * reason rather than throwing, and every caller ignores it.
 */
export async function ensureIdentity(externalId) {
  if (!externalId) return { linked: false, reason: "anonymous" };
  if (pushSupport() !== "supported") return { linked: false, reason: "unsupported" };
  // No permission means no subscription to bind. Asking for one here would turn
  // "you loaded the page" into a permission prompt, which is the invitation
  // card's job and its alone.
  if (permissionState() !== "granted") return { linked: false, reason: "nopermission" };

  // Two components mount this on the same page (the invitation card and the
  // dashboard toggle); collapsing them into one attempt is what keeps that from
  // being two logins racing over the same subscription.
  if (_identityInFlight) return _identityInFlight;
  const attempt = (async () => {
    try {
      const OneSignal = await loadOneSignal();
      const sub = OneSignal.User?.PushSubscription;
      const id = sub?.id || null;
      if (!id || !sub?.optedIn) return { linked: false, reason: "nosubscription" };

      const mark = `${externalId}:${id}`;
      if (readIdentityMark() === mark) return { linked: true, reason: "cached", id };

      // `User.externalId` is the SDK's own answer and the cheapest one; it is
      // read defensively because a build that does not expose it must fall
      // through to logging in rather than deciding the device is unbound.
      const bound = OneSignal.User?.externalId || null;
      if (String(bound || "") !== String(externalId)) {
        await OneSignal.login(String(externalId));
      }
      // Registered even when the SDK says it was already bound: this is the
      // call that re-syncs the tags, and — if `login()` did not take — the one
      // that lets the backend bind the device itself from the player id and the
      // session (services/push_tags.py `ensure_identity`).
      await registerPlayerId(id);
      writeIdentityMark(mark);
      return { linked: true, reason: bound ? "confirmed" : "relinked", id };
    } catch (e) {
      console.warn("[FixyDany push] could not confirm this device's identity:", e);
      return { linked: false, reason: "error", error: e };
    }
  })();
  _identityInFlight = attempt;
  attempt.finally(() => { _identityInFlight = null; });
  return attempt;
}

/** Tell our backend which player id this browser holds. */
export async function registerPlayerId(playerId) {
  if (!playerId) return false;
  await api.post("/notifications/push-subscribe", {
    player_id: playerId,
    platform: "web",
  });
  return true;
}

/**
 * Turn push on: permission if it is missing, opt in, register the id.
 *
 * Also the reconnection path. A browser that lost its subscription — cleared
 * data, a service worker that went away — has permission granted and no id,
 * and running exactly this sequence is what puts it back.
 */
export async function enablePush({ externalId } = {}) {
  const OneSignal = await loadOneSignal();
  // Bind this browser to OUR user id before subscribing. It is what lets the
  // backend tag the person rather than the device (services/push_tags.py):
  // every device they use collapses onto one OneSignal user, and a tag written
  // today survives a browser that re-subscribes tomorrow. Losing the binding is
  // not worth failing a subscription over — an untagged subscriber still
  // receives everything addressed to them by id.
  if (externalId) {
    try {
      await OneSignal.login(String(externalId));
    } catch (e) {
      console.warn("[FixyDany push] could not bind the external id:", e);
    }
  }
  if (permissionState() !== "granted") {
    await OneSignal.Notifications.requestPermission();
  }
  const permission = permissionState();
  if (permission === "denied") return { state: "denied", id: null };
  if (permission !== "granted") return { state: "nodialog", id: null };

  // optIn() is what reverses a previous optOut(); on a first subscription it is
  // the call that creates the record.
  await OneSignal.User.PushSubscription.optIn();
  const id = await waitForSubscriptionId(OneSignal);
  if (!id) return { state: "nosubscription", id: null };
  await registerPlayerId(id);
  if (externalId) writeIdentityMark(`${externalId}:${id}`);
  return { state: "on", id };
}

/**
 * Turn push off for real, on both sides.
 *
 * `optOut()` is what stops OneSignal sending to this device, and it is
 * reversible — the browser permission is untouched, so turning it back on never
 * asks again. Deleting our row is the second half: FixyMatch reads
 * `push_subscriptions` directly, so a row left behind would keep this device in
 * every audience even though OneSignal would refuse to deliver.
 */
export async function disablePush() {
  const OneSignal = await loadOneSignal();
  const id = OneSignal.User?.PushSubscription?.id || null;
  await OneSignal.User.PushSubscription.optOut();
  // The binding record goes with it. It is keyed by subscription id and the next
  // one will differ, but leaving it behind would let a second account on this
  // browser read someone else's mark as its own.
  try { localStorage.removeItem(IDENTITY_KEY); } catch { /* storage off */ }
  try {
    await api.post("/notifications/push-unsubscribe", id ? { player_id: id } : {});
  } catch (e) {
    // The device is already opted out at OneSignal, which is what the person
    // asked for. A stale row is a delivery that gets dropped, not one that
    // arrives, so this is worth reporting and not worth failing over.
    console.warn("[FixyDany push] could not drop the subscription row:", e);
  }
  return { state: "off", id: null };
}

/**
 * Take this device out of the leaving account's audience. Called by logout,
 * BEFORE the session ends, because the unsubscribe call is authenticated as
 * the account that is leaving.
 *
 * The bug this exists for (AUDIT.md 11, finding 41): `push_subscriptions` is
 * unique per (user, player), and logout used to post `/auth/logout` and nothing
 * else. Every account ever signed in on a phone kept that phone in its
 * audience — 7 of 15 devices in production on 2026-09-23 — so a push for one
 * account rang while another was open.
 *
 * Only this device's row goes, and the subscription is NOT opted out: the
 * browser permission belongs to the person holding the phone, and the next
 * account that signs in here is re-registered by `ensureIdentity` without being
 * asked again. `OneSignal.logout()` drops the external id, so tag audiences
 * (services/push_tags.py) stop naming the old account too.
 *
 * Capped in time and unable to fail: signing out must never wait on a CDN.
 */
export async function releaseDeviceOnLogout({ timeoutMs = 4000 } = {}) {
  if (pushSupport() !== "supported" || permissionState() !== "granted") return false;
  try { localStorage.removeItem(IDENTITY_KEY); } catch { /* storage off */ }
  const work = (async () => {
    const OneSignal = await loadOneSignal();
    const id = OneSignal.User?.PushSubscription?.id || null;
    if (id) await api.post("/notifications/push-unsubscribe", { player_id: id });
    try { await OneSignal.logout(); } catch { /* older SDK: the row is what matters */ }
    return !!id;
  })();
  const cap = new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs));
  try {
    return await Promise.race([work, cap]);
  } catch (e) {
    console.warn("[FixyDany push] could not release this device on logout:", e);
    return false;
  }
}

/**
 * The id does not exist the instant optIn() resolves.
 *
 * Subscribing is a round trip — service worker, push service, OneSignal — and
 * `PushSubscription.id` is null until it lands. Reading it immediately is how a
 * successful subscription reports itself as a failure, so this waits for the
 * change event with a poll behind it and a ceiling on both.
 */
function waitForSubscriptionId(OneSignal, timeoutMs = 10000) {
  const existing = OneSignal.User?.PushSubscription?.id;
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve) => {
    let done = false;
    const finish = (id) => {
      if (done) return;
      done = true;
      clearInterval(poll);
      clearTimeout(cap);
      try { OneSignal.User.PushSubscription.removeEventListener("change", onChange); } catch { /* best effort */ }
      resolve(id || null);
    };
    const onChange = (ev) => finish(ev?.current?.id || OneSignal.User?.PushSubscription?.id);
    try { OneSignal.User.PushSubscription.addEventListener("change", onChange); } catch { /* polled instead */ }
    const poll = setInterval(() => {
      const id = OneSignal.User?.PushSubscription?.id;
      if (id) finish(id);
    }, 400);
    const cap = setTimeout(() => finish(OneSignal.User?.PushSubscription?.id), timeoutMs);
  });
}

// --- following a notification -------------------------------------------
//
// **The tap has to go somewhere.** A push carries a `url`, and OneSignal's
// service worker decides what to do with it: if a tab is already open on this
// origin it focuses that tab rather than navigating it, which is a reasonable
// default and reads, from the person's side, as a notification whose tap does
// nothing at all. That is what Daniel saw on 2026-09-02 — the broadcast
// arrived, the click landed on a focused tab still showing whatever page it was
// already on.
//
// So the destination travels twice. `url` is for the browser that has no tab
// open, where the service worker opens a window and there is nothing here to
// run. `data.path` is for the tab that is already open, and this listener is
// what follows it — the same in-app destination the notification bell links to
// (components/notifications/NotificationBell.jsx), reached the way that bell
// reaches it: an internal path, never an absolute URL.
//
// A full navigation rather than a router push: this module is loaded outside
// the router (PushOptIn is mounted beside it in App.js), so there is no
// `useNavigate` to call here, and a reload on a notification tap is a cost
// nobody notices. What they would notice is nothing happening.

let _clickListenerAttached = false;

function attachClickNavigation(OneSignal) {
  if (_clickListenerAttached) return;
  _clickListenerAttached = true;
  try {
    OneSignal.Notifications?.addEventListener?.("click", (event) => {
      try {
        const data =
          event?.notification?.additionalData || event?.notification?.data || {};
        const target = internalPath(data.path) || internalPath(event?.result?.url);
        if (!target) return;
        // Already there: focusing the tab was the right answer after all, and
        // reloading the page the person is looking at would be worse than
        // doing nothing.
        const here = window.location.pathname + window.location.search;
        if (target === here) return;
        window.location.assign(target);
      } catch (e) {
        console.warn("[FixyDany push] could not follow the notification:", e);
      }
    });
  } catch {}
}

/**
 * A destination reduced to a path this app can navigate to, or null.
 *
 * Accepts our own absolute URLs — the payload carries one for the service
 * worker and it is the natural fallback when `data.path` is absent — and
 * refuses everything else. The server already refuses to store an external
 * destination (`push_broadcasts_target_internal`); this is the same rule on the
 * other side of the wire, because a click handler that will navigate anywhere a
 * payload names is one compromised notification away from being a redirector.
 */
function internalPath(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

// --- the FixyMatch boost chime ------------------------------------------
//
// Moved out of PushOptIn.jsx with the loader: it has to be attached once per
// page, and the component that used to own it is no longer the only thing that
// brings the SDK up.

let _boostListenerAttached = false;
function attachBoostChime(OneSignal) {
  if (_boostListenerAttached) return;
  _boostListenerAttached = true;
  try {
    OneSignal.Notifications?.addEventListener?.("foregroundWillDisplay", (event) => {
      try {
        const data = event?.notification?.additionalData || event?.notification?.data || {};
        if (data.type === "fixymatch_boost" || data.sound === "fixymatch") {
          _playBoostChime();
        }
      } catch {}
    });
  } catch {}
}

// Synthesize a distinctive 3-tone "FixyBee priority" chime via Web Audio API.
// No audio asset required; ~250ms total.
function _playBoostChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [{ f: 880, t: 0 }, { f: 1175, t: 0.09 }, { f: 1568, t: 0.18 }].forEach(({ f, t }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(f, now + t);
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.4, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.2);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 600);
  } catch {}
}

// --- the two moments worth asking at ------------------------------------
//
// A FixyBee finishing onboarding and a client whose first job has just gone
// live are the two points where push stops being an abstraction: one is about
// to wait for jobs, the other for quotes. Both pages navigate away the instant
// they succeed, so the moment is ARMED here and collected by the invitation
// card on the destination — which is mounted once, outside the router, and is
// already the thing that owns this conversation.
//
// Once per reason, forever. `fd_push_invite_seen_<reason>` is what makes "does
// not insist" enforceable rather than aspirational, and it is deliberately
// separate from the card's own dismissed/subscribed state: someone who closed
// the generic invitation months ago should still hear the sentence that is
// actually about them — but only one time.

const INVITE_KEY = "fd_push_invite";

export const INVITE_PROVIDER_ONBOARDING = "provider_onboarding";
export const INVITE_CLIENT_FIRST_JOB = "client_first_job";

/**
 * Fired on `window` when a moment is armed (T20-M2).
 *
 * The card decides once, when the session's user is first known — and in
 * every flow that arms a moment the user is ALREADY known by then: the job
 * form, onboarding and Dany's thread are all signed-in screens. So an armed
 * reason sat in sessionStorage until the next full page load, and was lost
 * with the tab. This is how the card hears it the moment it happens.
 */
export const PUSH_INVITE_EVENT = "fd:push-invite";

export function armPushInvite(reason) {
  try {
    if (localStorage.getItem(`fd_push_invite_seen_${reason}`)) return;
    sessionStorage.setItem(INVITE_KEY, reason);
  } catch { /* storage disabled: the generic card still offers */ }
  try { window.dispatchEvent(new Event(PUSH_INVITE_EVENT)); } catch { /* no window */ }
}

/** Read and consume the armed reason, marking it spent for good. */
export function takePushInvite() {
  try {
    const reason = sessionStorage.getItem(INVITE_KEY);
    if (!reason) return null;
    sessionStorage.removeItem(INVITE_KEY);
    if (localStorage.getItem(`fd_push_invite_seen_${reason}`)) return null;
    localStorage.setItem(`fd_push_invite_seen_${reason}`, "1");
    return reason;
  } catch {
    return null;
  }
}
